// Sistema di logging avanzato per Gemini AI
// Traccia tutte le chiamate, errori, rate limits e performance

class GeminiLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500; // Mantieni ultimi 500 log
    this.enabled = true;
    
    // Esponi globalmente per accesso da console
    if (typeof window !== 'undefined') {
      window.geminiLogger = this;
    }
  }

  /**
   * Log una chiamata API Gemini
   */
  logApiCall(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'api_call',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('📡 API CALL', logEntry);
  }

  /**
   * Log una risposta API Gemini di successo
   */
  logApiSuccess(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'api_success',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('✅ SUCCESS', logEntry, 'success');
  }

  /**
   * Log un errore API Gemini
   */
  logApiError(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'api_error',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('❌ ERROR', logEntry, 'error');
  }

  /**
   * Log un rate limit
   */
  logRateLimit(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'rate_limit',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('⚠️ RATE LIMIT', logEntry, 'warn');
  }

  /**
   * Log un fallback di modello
   */
  logModelFallback(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'model_fallback',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('🔄 MODEL FALLBACK', logEntry, 'warn');
  }

  /**
   * Log parsing JSON
   */
  logJsonParsing(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'json_parsing',
      ...data
    };
    
    this._addLog(logEntry);
    
    if (data.success) {
      this._consoleLog('✅ JSON PARSED', logEntry, 'success');
    } else {
      this._consoleLog('❌ JSON PARSE ERROR', logEntry, 'error');
    }
  }

  /**
   * Log cache hit/miss
   */
  logCache(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'cache',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog(data.hit ? '💾 CACHE HIT' : '🔍 CACHE MISS', logEntry);
  }

  /**
   * Log performance metrics
   */
  logPerformance(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'performance',
      ...data
    };
    
    this._addLog(logEntry);
    this._consoleLog('📊 PERFORMANCE', logEntry);
  }

  /**
   * Log confronto Gemini vs Spotify - traccia cosa ha suggerito Gemini vs cosa Spotify ha trovato
   */
  logGeminiSpotifyComparison(data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'gemini_spotify_comparison',
      ...data
    };
    
    this._addLog(logEntry);
    
    // Log dettagliato solo se non trovato (invenzione di Gemini)
    if (!data.spotifyFound) {
      this._consoleLog('⚠️ GEMINI INVENZIONE', logEntry, 'warn');
    } else if (data.mismatch) {
      this._consoleLog('🔄 GEMINI MISMATCH', logEntry, 'warn');
    } else {
      this._consoleLog('✅ GEMINI SPOTIFY MATCH', logEntry, 'success');
    }
  }

  /**
   * Aggiunge un log alla lista
   */
  _addLog(logEntry) {
    if (!this.enabled) return;
    
    this.logs.push(logEntry);
    
    // Mantieni solo gli ultimi maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Output su console con formattazione
   */
  _consoleLog(prefix, data, type = 'info') {
    if (!this.enabled) return;
    
    const colors = {
      info: 'background: #2196F3; color: white;',
      success: 'background: #4CAF50; color: white;',
      warn: 'background: #FF9800; color: white;',
      error: 'background: #F44336; color: white;'
    };
    
    const style = colors[type] || colors.info;
    
    console.groupCollapsed(
      `%c${prefix}%c ${data.context || ''} ${data.artistName || ''} ${data.model || ''}`,
      style + ' padding: 4px 8px; border-radius: 3px; font-weight: bold;',
      'color: inherit; font-weight: normal;'
    );
    console.log('Timestamp:', data.timestamp);
    console.log('Type:', data.type);
    
    // Log tutti i campi
    Object.keys(data).forEach(key => {
      if (key !== 'timestamp' && key !== 'type') {
        console.log(`${key}:`, data[key]);
      }
    });
    
    console.groupEnd();
  }

  /**
   * Ottieni tutti i log
   */
  getAllLogs() {
    return [...this.logs];
  }

  /**
   * Ottieni log filtrati per tipo
   */
  getLogsByType(type) {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Ottieni log degli errori
   */
  getErrors() {
    return this.logs.filter(log => 
      log.type === 'api_error' || 
      log.type === 'rate_limit' ||
      (log.type === 'json_parsing' && !log.success)
    );
  }

  /**
   * Ottieni l'ultimo errore di parsing JSON
   */
  getLastJsonError() {
    const jsonErrors = this.logs
      .filter(log => log.type === 'json_parsing' && !log.success)
      .reverse();
    
    if (jsonErrors.length === 0) {
      console.log('Nessun errore di parsing JSON trovato');
      return null;
    }
    
    const lastError = jsonErrors[0];
    
    console.log('%c🔍 ULTIMO ERRORE JSON PARSING', 'background: #F44336; color: white; padding: 8px; font-weight: bold; font-size: 14px;');
    console.log('');
    console.log('Artista:', lastError.artistName || lastError.context);
    console.log('Modello:', lastError.model);
    console.log('Errore:', lastError.error);
    console.log('Timestamp:', lastError.timestamp);
    console.log('');
    console.log('📄 INIZIO TESTO (primi 500 char):');
    console.log(lastError.textSample);
    console.log('');
    if (lastError.textEnd) {
      console.log('📄 FINE TESTO (ultimi 200 char):');
      console.log(lastError.textEnd);
      console.log('');
    }
    console.log('📊 Lunghezza totale:', lastError.textLength);
    console.log('🔚 Finish Reason:', lastError.finishReason || 'N/A');
    console.log('');
    
    return lastError;
  }

  /**
   * Ottieni statistiche
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      byType: {},
      byModel: {},
      errors: 0,
      rateLimits: 0,
      successes: 0,
      averageResponseTime: 0,
      totalCalls: 0
    };
    
    let totalResponseTime = 0;
    let responseTimeCount = 0;
    
    this.logs.forEach(log => {
      // Conta per tipo
      stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;
      
      // Conta per modello
      if (log.model) {
        stats.byModel[log.model] = (stats.byModel[log.model] || 0) + 1;
      }
      
      // Conta errori
      if (log.type === 'api_error') stats.errors++;
      if (log.type === 'rate_limit') stats.rateLimits++;
      if (log.type === 'api_success') stats.successes++;
      if (log.type === 'api_call') stats.totalCalls++;
      
      // Calcola response time medio
      if (log.responseTime) {
        totalResponseTime += log.responseTime;
        responseTimeCount++;
      }
    });
    
    if (responseTimeCount > 0) {
      stats.averageResponseTime = Math.round(totalResponseTime / responseTimeCount);
    }
    
    return stats;
  }

  /**
   * Stampa report completo
   */
  printReport() {
    const stats = this.getStats();
    const errors = this.getErrors();
    
    console.log('\n%c═══════════════════════════════════════════════', 'color: #2196F3; font-weight: bold;');
    console.log('%c   📊 GEMINI API - REPORT DETTAGLIATO', 'color: #2196F3; font-weight: bold; font-size: 16px;');
    console.log('%c═══════════════════════════════════════════════\n', 'color: #2196F3; font-weight: bold;');
    
    console.log('%c📈 STATISTICHE GENERALI', 'color: #4CAF50; font-weight: bold; font-size: 14px;');
    console.log(`   Total Logs: ${stats.total}`);
    console.log(`   Total API Calls: ${stats.totalCalls}`);
    console.log(`   Successes: ${stats.successes}`);
    console.log(`   Errors: ${stats.errors}`);
    console.log(`   Rate Limits: ${stats.rateLimits}`);
    console.log(`   Avg Response Time: ${stats.averageResponseTime}ms\n`);
    
    console.log('%c🤖 UTILIZZO MODELLI', 'color: #FF9800; font-weight: bold; font-size: 14px;');
    Object.keys(stats.byModel).forEach(model => {
      console.log(`   ${model}: ${stats.byModel[model]} chiamate`);
    });
    console.log('');
    
    console.log('%c📋 LOG PER TIPO', 'color: #9C27B0; font-weight: bold; font-size: 14px;');
    Object.keys(stats.byType).forEach(type => {
      console.log(`   ${type}: ${stats.byType[type]}`);
    });
    console.log('');
    
    if (errors.length > 0) {
      console.log('%c❌ ULTIMI ERRORI', 'color: #F44336; font-weight: bold; font-size: 14px;');
      errors.slice(-10).forEach((error, i) => {
        console.log(`   ${i + 1}. [${error.timestamp}] ${error.model || 'N/A'} - ${error.errorMessage || error.status || 'Unknown error'}`);
      });
      console.log('');
    }
    
    console.log('%c💡 COMANDI DISPONIBILI', 'color: #00BCD4; font-weight: bold; font-size: 14px;');
    console.log('   window.geminiLogger.printReport() - Mostra questo report');
    console.log('   window.geminiLogger.printGeminiSpotifyReport() - Report Gemini vs Spotify (invenzioni)');
    console.log('   window.geminiLogger.comparePerformance() - Confronta tempi V1 vs V2');
    console.log('   window.geminiLogger.getAllLogs() - Ottieni tutti i log');
    console.log('   window.geminiLogger.getErrors() - Ottieni solo gli errori');
    console.log('   window.geminiLogger.getLastJsonError() - Mostra ultimo errore JSON con dettagli');
    console.log('   window.geminiLogger.getStats() - Ottieni statistiche');
    console.log('   window.geminiLogger.clear() - Pulisci tutti i log');
    console.log('   window.geminiLogger.exportToJson() - Esporta log in JSON');
    console.log('   window.geminiLogger.enabled = false - Disabilita logging\n');
    
    console.log('%c═══════════════════════════════════════════════\n', 'color: #2196F3; font-weight: bold;');
  }

  /**
   * Pulisci tutti i log
   */
  clear() {
    this.logs = [];
    console.log('[GeminiLogger] All logs cleared');
  }

  /**
   * Esporta log in formato JSON
   */
  exportToJson() {
    const data = {
      exportDate: new Date().toISOString(),
      stats: this.getStats(),
      logs: this.getAllLogs()
    };
    
    const json = JSON.stringify(data, null, 2);
    
    // Crea un blob e scarica
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gemini-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('[GeminiLogger] Logs exported to JSON file');
  }

  /**
   * Abilita/disabilita logging
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`[GeminiLogger] Logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Confronta performance tra diverse versioni/strategie
   */
  comparePerformance() {
    const performances = this.logs.filter(log => log.type === 'performance');
    
    if (performances.length === 0) {
      console.log('Nessuna performance da confrontare');
      return;
    }
    
    console.log('%c⚡ CONFRONTO PERFORMANCE', 'background: #FF9800; color: white; padding: 8px 16px; border-radius: 4px; font-weight: bold; font-size: 14px;');
    console.log('');
    
    // Raggruppa per artista
    const byArtist = {};
    performances.forEach(perf => {
      const artist = perf.artistName || 'unknown';
      if (!byArtist[artist]) {
        byArtist[artist] = [];
      }
      byArtist[artist].push(perf);
    });
    
    // Mostra confronto per ogni artista
    Object.keys(byArtist).forEach(artist => {
      const perfs = byArtist[artist];
      
      console.log(`%c🎵 ${artist}`, 'color: #2196F3; font-weight: bold; font-size: 13px;');
      
      perfs.forEach(perf => {
        const strategy = perf.strategy === 'two_phase' ? 'Two-Phase' : 'Single-Phase';
        const promptV = `V${perf.promptVersion || '?'}`;
        const time = perf.totalTime || perf.responseTime;
        const model = perf.model || `${perf.phase1Model} + ${perf.phase2Model}`;
        
        console.log(`   ${strategy} ${promptV}: ${time}ms - ${perf.artistCount} artisti - ${model}`);
        
        if (perf.strategy === 'two_phase') {
          console.log(`      └─ Fase 1: ${perf.phase1Time}ms | Fase 2: ${perf.phase2Time}ms`);
        }
      });
      
      console.log('');
    });
    
    // Statistiche aggregate
    const singlePhase = performances.filter(p => p.strategy !== 'two_phase');
    const twoPhase = performances.filter(p => p.strategy === 'two_phase');
    
    if (singlePhase.length > 0) {
      const avgSingle = singlePhase.reduce((sum, p) => sum + (p.totalTime || p.responseTime), 0) / singlePhase.length;
      console.log(`📊 Media Single-Phase: ${Math.round(avgSingle)}ms (${singlePhase.length} chiamate)`);
    }
    
    if (twoPhase.length > 0) {
      const avgTwo = twoPhase.reduce((sum, p) => sum + p.totalTime, 0) / twoPhase.length;
      console.log(`📊 Media Two-Phase: ${Math.round(avgTwo)}ms (${twoPhase.length} chiamate)`);
    }
    
    console.log('');
  }

  /**
   * Stampa report completo Gemini vs Spotify - mostra tutte le invenzioni
   */
  printGeminiSpotifyReport() {
    const comparisons = this.logs.filter(log => log.type === 'gemini_spotify_comparison');
    
    if (comparisons.length === 0) {
      console.log('%c⚠️ Nessun confronto Gemini vs Spotify trovato', 'color: #FF9800; font-weight: bold;');
      console.log('Esegui alcune ricerche per vedere il report.');
      return;
    }
    
    console.log('\n%c═══════════════════════════════════════════════', 'color: #9C27B0; font-weight: bold;');
    console.log('%c   🔍 GEMINI vs SPOTIFY - REPORT COMPLETO', 'color: #9C27B0; font-weight: bold; font-size: 16px;');
    console.log('%c═══════════════════════════════════════════════\n', 'color: #9C27B0; font-weight: bold;');
    
    // Raggruppa per query/searchType
    const byQuery = {};
    comparisons.forEach(comp => {
      const key = comp.query || comp.searchType || 'unknown';
      if (!byQuery[key]) {
        byQuery[key] = [];
      }
      byQuery[key].push(comp);
    });
    
    // Statistiche
    const total = comparisons.length;
    const notFound = comparisons.filter(c => !c.spotifyFound).length;
    const mismatches = comparisons.filter(c => c.mismatch).length;
    const matches = comparisons.filter(c => c.spotifyFound && !c.mismatch).length;
    
    console.log('%c📊 STATISTICHE GENERALI', 'color: #2196F3; font-weight: bold; font-size: 14px;');
    console.log(`   Totale confronti: ${total}`);
    console.log(`   ✅ Trovati su Spotify: ${matches} (${Math.round(matches/total*100)}%)`);
    console.log(`   ⚠️  Non trovati (invenzioni): ${notFound} (${Math.round(notFound/total*100)}%)`);
    console.log(`   🔄 Mismatch (nomi diversi): ${mismatches} (${Math.round(mismatches/total*100)}%)\n`);
    
    // Per ogni query, mostra i dettagli
    Object.keys(byQuery).forEach(query => {
      const comps = byQuery[query];
      const notFoundInQuery = comps.filter(c => !c.spotifyFound);
      const mismatchesInQuery = comps.filter(c => c.mismatch);
      
      if (notFoundInQuery.length > 0 || mismatchesInQuery.length > 0) {
        console.log(`%c🔍 Query: "${query}"`, 'color: #FF9800; font-weight: bold; font-size: 13px;');
        console.log(`   Tipo: ${comps[0].searchType || 'N/A'} | Totale: ${comps.length}`);
        
        if (notFoundInQuery.length > 0) {
          console.log(`\n   %c❌ INVENZIONI DI GEMINI (${notFoundInQuery.length}):`, 'color: #F44336; font-weight: bold;');
          notFoundInQuery.forEach((comp, i) => {
            console.log(`   ${i + 1}. Posizione ${comp.position}:`);
            if (comp.searchType === 'album') {
              console.log(`      Gemini: "${comp.geminiAlbum}" by "${comp.geminiArtist}" (match: ${comp.geminiMatch})`);
              console.log(`      Spotify: ❌ NON TROVATO`);
            } else if (comp.searchType === 'track') {
              console.log(`      Gemini: "${comp.geminiTrack}" by "${comp.geminiArtist}" (match: ${comp.geminiMatch})`);
              console.log(`      Spotify: ❌ NON TROVATO`);
            } else {
              console.log(`      Gemini: "${comp.geminiName}" (match: ${comp.geminiMatch})`);
              console.log(`      Spotify: ❌ NON TROVATO`);
            }
          });
        }
        
        if (mismatchesInQuery.length > 0) {
          console.log(`\n   %c🔄 MISMATCH (${mismatchesInQuery.length}):`, 'color: #FF9800; font-weight: bold;');
          mismatchesInQuery.forEach((comp, i) => {
            console.log(`   ${i + 1}. Posizione ${comp.position}:`);
            if (comp.searchType === 'album') {
              console.log(`      Gemini: "${comp.geminiAlbum}" by "${comp.geminiArtist}"`);
              console.log(`      Spotify: "${comp.spotifyAlbum}" by "${comp.spotifyArtist}"`);
            } else if (comp.searchType === 'track') {
              console.log(`      Gemini: "${comp.geminiTrack}" by "${comp.geminiArtist}"`);
              console.log(`      Spotify: "${comp.spotifyTrack}" by "${comp.spotifyArtist}"`);
            } else {
              console.log(`      Gemini: "${comp.geminiName}"`);
              console.log(`      Spotify: "${comp.spotifyName}"`);
            }
          });
        }
        
        console.log('');
      }
    });
    
    // Mostra tutti i confronti in formato tabella
    console.log('%c📋 DETTAGLIO COMPLETO (primi 50):', 'color: #9C27B0; font-weight: bold; font-size: 14px;');
    console.table(comparisons.slice(0, 50).map(c => ({
      Pos: c.position,
      Tipo: c.searchType,
      'Gemini': c.searchType === 'album' 
        ? `${c.geminiAlbum} by ${c.geminiArtist}`
        : c.searchType === 'track'
        ? `${c.geminiTrack} by ${c.geminiArtist}`
        : c.geminiName,
      'Spotify': c.spotifyFound 
        ? (c.searchType === 'album' 
          ? `${c.spotifyAlbum} by ${c.spotifyArtist}`
          : c.searchType === 'track'
          ? `${c.spotifyTrack} by ${c.spotifyArtist}`
          : c.spotifyName)
        : '❌ NON TROVATO',
      Match: c.geminiMatch,
      Status: c.spotifyFound ? (c.mismatch ? '🔄 MISMATCH' : '✅ OK') : '❌ INVENZIONE'
    })));
    
    console.log('\n%c═══════════════════════════════════════════════\n', 'color: #9C27B0; font-weight: bold;');
  }
}

// Singleton instance
const geminiLogger = new GeminiLogger();

// Log inizializzazione
if (typeof window !== 'undefined') {
  console.log('%c🔍 GEMINI LOGGER ATTIVO', 'background: #2196F3; color: white; padding: 8px 16px; border-radius: 4px; font-weight: bold; font-size: 14px;');
  console.log('%cDigita window.geminiLogger.printReport() per vedere il report completo', 'color: #666; font-style: italic;');
}

export default geminiLogger;

